import { logger } from "@/core/logger";
import { initializeAppStore, subscribeToAppStorePersistence, useAppStore } from "@/core/stores/appStore";
import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";

// ✅ Define authentication context type
interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (redirectTo?: string) => void;
    logout: () => Promise<void>;
}

// ✅ Create authentication context
const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [isInitialized, setIsInitialized] = useState(false);

    // Subscribe to app store
    const auth = useAppStore((state) => state.auth);
    const isAuthenticated = auth.isAuthenticated;
    const clearAuth = useAppStore((state) => state.clearAuth);
    const hasHydrated = useAppStore((state) => state._hasHydrated);

    // Initialize store on mount
    useEffect(() => {
        let mounted = true;

        const initialize = async () => {
            try {
                // Initialize store from storage
                await initializeAppStore();

                // Subscribe to persistence
                subscribeToAppStorePersistence();

                if (mounted) {
                    setIsInitialized(true);
                    logger.info('AuthContext', 'App store initialized');
                }
            } catch (error) {
                logger.error('AuthContext', 'Failed to initialize', error);
                if (mounted) {
                    setIsInitialized(true);
                }
            }
        };

        initialize();

        return () => {
            mounted = false;
        };
    }, []);

    // ✅ Login function (Just update auth state in store)
    const login = (redirectTo?: string) => {
        // Auth state is managed by appStore now
        // Accept redirectTo to comply with navigation rules; navigation occurs at route level
        logger.info('AuthContext', 'Login called (auth managed by appStore)', { redirectTo });
    };

    // ✅ Logout function (Just update auth state in store)
    const logout = useCallback(async () => {
        try {
            await clearAuth(); // Clear auth state in appStore (this also clears storage)
            logger.info('AuthContext', 'User logged out');
        } catch (error) {
            logger.error('AuthContext', 'Failed to logout', error);
        }
    }, [clearAuth]);

    // Loading state: waiting for store hydration
    const isLoading = !isInitialized || !hasHydrated;

    return (
        <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

// ✅ Hook to use authentication state
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
